import React, { useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Turnstile from "@/components/Turnstile";
import { toast } from "sonner";

const Login = () => {
  const { login } = useApp();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tsToken, setTsToken] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const onTsToken = useCallback((t) => setTsToken(t), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password, tsToken);
      toast.success("Welcome back");
      // Role-based redirect
      const dest = u && u.role && u.role !== "customer" ? "/admin/dashboard" : "/";
      navigate(dest);
    } catch (err) {
      const status = err?.response?.status;
      const serverDetail = err?.response?.data?.detail;
      if (status === 429) {
        toast.error("Too many attempts", {
          description: serverDetail || "Please wait a minute and try again.",
        });
      } else if (status === 401) {
        toast.error("Sign in failed", { description: "Invalid email or password." });
      } else {
        toast.error("Sign in failed", {
          description: serverDetail || "Something went wrong. Please try again.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-6 py-24">
        <div className="overline text-accent">Sign in</div>
        <h1 className="font-display text-5xl mt-3 mb-10 leading-none tracking-tighter">Welcome back.</h1>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="email" className="overline text-muted-foreground">Email</Label>
            <Input
              id="email"
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="rounded-none mt-2 h-12"
            />
          </div>
          <div>
            <Label htmlFor="password" className="overline text-muted-foreground">Password</Label>
            <Input
              id="password"
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="rounded-none mt-2 h-12"
            />
          </div>
          <Turnstile onToken={onTsToken} action="login" />
          <Button
            type="submit"
            data-testid="login-submit-btn"
            disabled={loading}
            className="w-full rounded-none h-12 bg-primary"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <div className="mt-4 text-right">
          <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-accent hover:underline">
            Forgot password?
          </Link>
        </div>

        <div className="my-8 relative flex items-center justify-center">
          <div className="absolute inset-x-0 border-t border-border" />
          <span className="relative bg-background px-4 overline text-muted-foreground">Or continue with</span>
        </div>

        <Button
          onClick={handleGoogle}
          data-testid="google-login-btn"
          variant="outline"
          className="w-full rounded-none h-12 border-foreground"
        >
          Continue with Google
        </Button>

        <p className="mt-8 text-sm text-muted-foreground text-center">
          Don't have an account?{" "}
          <Link to="/register" className="text-accent hover:underline" data-testid="link-register">Create one</Link>
        </p>
      </div>
    </Layout>
  );
};

export default Login;
